// Ported from microsites/womens-conference/src/components/Schedule.tsx.
// Changes: the run of show, topics and lunch menu are MP's (lib/views.ts);
// picks are the household person's live choices and save through /choices,
// optimistically, instead of the microsite's mock store; Now/Next reads the
// experience clock (wall time, so the time machine moves it); a guest's
// Choose opens the shared registration sheet.
import { useState } from 'react';
import type { ChoiceChange, ChoicePerson } from '@/lib/types';
import type {
  AgendaItem,
  ConferenceConfig,
  ConferencePhase,
  FullnessState,
} from '../lib/conference';
import { fullness, registrationOpen } from '../lib/conference';
import type { SeatCount } from '../lib/conference';
import { clockLabel } from '../lib/format';
import type { SpeakerView, TopicView } from '../lib/views';
import { Avatar } from './Avatar';
import { SlotChoices } from './SlotChoices';
import { usePicks } from '../lib/usePicks';
import { Sprig, Swash } from './Botanical';
import { FullnessBadge } from './FullnessBadge';
import { LunchPicker } from './LunchPicker';
import { LunchThumb } from './Lunch';
import { LunchThumbs, TopicFaces } from './ChoicePreview';
import { Sheet, SheetBand } from './Sheet';
import { TopicTitle } from './TopicTitle';
import { useMeals } from './MealsProvider';

/**
 * The schedule IS the page: every decision lives on the row where it
 * happens. The Breakout Session One row shows YOUR topic, room, and speaker,
 * and changing it opens that slot's picker; lunch works the same way. In the
 * live phase the list carries now/next markers against the venue clock
 * (simulated when previewing), re-derived every half minute.
 */
type AgendaStatus = 'past' | 'now' | 'next' | 'upcoming';

/** The microsite's agendaStatuses, on MP wall clock: no offsets to anchor. */
function agendaStatuses(
  config: ConferenceConfig,
  items: AgendaItem[],
  now: string
): AgendaStatus[] {
  const at = (item: AgendaItem, hhmm: string | undefined) => {
    const day = config.days.find((d) => d.key === item.day);
    return day && hhmm ? `${day.date}T${hhmm}:00` : null;
  };
  const statuses: AgendaStatus[] = items.map((item) => {
    const start = at(item, item.start);
    const end = at(item, item.end) ?? start;
    if (!start) return 'upcoming';
    if (end && now >= end) return 'past';
    if (now >= start) return 'now';
    return 'upcoming';
  });
  const firstUpcoming = statuses.findIndex((s) => s === 'upcoming');
  if (firstUpcoming >= 0) statuses[firstUpcoming] = 'next';
  return statuses;
}

export function Schedule({
  config,
  topics,
  speakers,
  person,
  locked,
  saveChoice,
  onRegister,
  isRegistered,
  phase,
  capacity,
  nowIso,
  agenda,
}: {
  config: ConferenceConfig;
  topics: TopicView[];
  speakers: Record<string, SpeakerView>;
  /** Whose picks the rows show: the viewer's own place, or null. */
  person: ChoicePerson | null;
  /** Past Choices_Close_Date (or the start): picks show but stop changing. */
  locked: boolean;
  saveChoice: (change: ChoiceChange) => Promise<string | null>;
  onRegister: () => void;
  /** A guest sees the same schedule with no picks and no pick controls. */
  isRegistered: boolean;
  phase: ConferencePhase;
  capacity?: SeatCount | null;
  /** Wall clock to judge now/next against; null outside the live phase. The
   *  experience clock ticks every 30s, so no timer of its own here. */
  nowIso: string | null;
  /** The run of show, from MP's Agenda_Elements. Empty falls back to the config. */
  agenda?: AgendaItem[];
}) {
  const meals = useMeals();
  const [openSlot, setOpenSlot] = useState<number | null>(null);
  const [lunchOpen, setLunchOpen] = useState(false);
  const {
    pickFor: picked,
    lunch: lunchPick,
    setBreakout,
    setLunch,
    problem,
  } = usePicks(person, topics, meals, saveChoice);

  const items = agenda?.length ? agenda : config.agenda;
  const statuses: AgendaStatus[] | null = nowIso ? agendaStatuses(config, items, nowIso) : null;

  const editable =
    isRegistered &&
    !locked &&
    !!person?.canChange &&
    (phase === 'registration' || phase === 'preEvent');

  // Flag-football rule: a guest sees the same actionable controls, they just
  // lead to registration instead of writing a pick.
  const guestCanRegister = !isRegistered && registrationOpen(phase, capacity);
  // Two named topics beat a count. A count says there are some; the names say
  // which, and one of them is the reason she signs up.
  const topicTease =
    topics.length > 2
      ? `${topics[0]!.shortTitle}, ${topics[1]!.shortTitle} and ${topics.length - 2} more`
      : `${topics.length} topics to choose from`;
  const pickFor = (slot: number) => (isRegistered ? picked(slot) : undefined);
  const topicFor = (slot: number) => topics.find((t) => t.slug === pickFor(slot));
  const lunchValue = isRegistered ? lunchPick : null;
  const lunchOption = lunchValue ? meals.options.find((o) => o.value === lunchValue) : undefined;
  const lunchLabel = lunchValue
    ? (meals.options.find((o) => o.value === lunchValue)?.label ?? lunchValue)
    : null;

  const nowItem = statuses ? items[statuses.indexOf('now')] : undefined;
  const nextItem = statuses ? items[statuses.indexOf('next')] : undefined;
  const describe = (item: AgendaItem) =>
    item.kind === 'breakout' && item.slot
      ? `${item.title}: ${topicFor(item.slot)?.shortTitle ?? 'pick a topic'}`
      : item.title;
  const locate = (item: AgendaItem) =>
    item.kind === 'breakout' && item.slot
      ? (topicFor(item.slot)?.room ?? 'Room to be announced')
      : (item.room ?? config.venue.name);

  const openTopicSlot = openSlot !== null ? openSlot : null;
  const slotGroup = config.breakoutGroups.find((g) => g.slot === openTopicSlot);

  return (
    <div className="flex flex-col gap-10">
      {phase === 'live' && (nowItem || nextItem) ? (
        <div className="wm-card border-wm-marigold flex flex-col gap-1 border-2 p-5">
          {nowItem ? (
            <p className="text-base">
              <span
                className="bg-wm-marigold mr-2 inline-block h-2 w-2 animate-pulse rounded-full align-middle"
                aria-hidden
              />
              <span className="wm-kicker mr-2">Now</span>
              <span className="font-semibold">{describe(nowItem)}</span>
              <span className="text-wm-sage-500"> · {locate(nowItem)}</span>
            </p>
          ) : null}
          {nextItem ? (
            <p className="text-wm-sage-700 text-sm">
              <span className="wm-kicker mr-2">Next</span>
              {describe(nextItem)} · {clockLabel(nextItem.start)} · {locate(nextItem)}
            </p>
          ) : null}
        </div>
      ) : null}

      {config.days.map((day) => (
        <section key={day.key} id={day.key}>
          <h2 className="wm-display text-4xl">
            {day.label}
            <span className="wm-display text-wm-sage-500 ml-3 align-middle text-lg italic">
              {new Date(`${day.date}T12:00:00`).toLocaleDateString('en-US', { month: 'long' })}{' '}
              {Number(day.date.slice(-2))}
            </span>
          </h2>
          <Swash className="text-wm-sage-300 mt-1 -ml-1" />

          {/* No cards down here: one pen line down the margin, entries
              hanging off it, dotted rules between. A program, not a table. */}
          <ol className="relative mt-4 pl-9">
            <span aria-hidden className="wm-stem absolute top-3 bottom-3 left-[7px] w-px" />
            {items.map((item, index) => {
              if (item.day !== day.key) return null;
              const status = statuses?.[index] ?? null;
              const dim = status === 'past' ? 'opacity-40' : '';
              /**
               * CENTRED, NOT BASELINE-ALIGNED.
               *
               * `items-baseline` lines the time up with the first baseline of
               * the title beside it, which looks right only while every row is
               * the same height. They are not: a row with a speaker's face is
               * taller than one without, a two-line blurb is taller than a
               * one-line room name, and the display serif sits differently from
               * the sans on the quiet rows. So the time wandered — level with
               * the title on "Doors open", stranded near the bottom on
               * "Grounded in Christ" and "Lunch".
               *
               * Centring both sides makes the time sit in the middle of its row
               * whatever is next to it, which is what a printed programme does.
               * `sm:` prefixed, because on a phone the row stacks and
               * `items-center` on a column would centre the text horizontally.
               */
              const rowClass = `relative flex w-full flex-col gap-1 py-5 text-left sm:flex-row sm:items-center sm:gap-6 ${
                status === 'now' ? 'text-wm-sage-950' : ''
              }`;
              const mark = (
                <span
                  aria-hidden
                  // Centred with the row for the same reason. A fixed offset
                  // from the top left the sprig near the title on a short row
                  // and adrift above the content on a tall one.
                  className={`absolute top-1/2 -left-9 -translate-y-1/2 ${
                    status === 'now' ? 'text-wm-marigold-deep' : 'text-wm-sage-300'
                  }`}
                >
                  <Sprig kind={MARKS[item.kind ?? 'session'] ?? 'leaf'} />
                </span>
              );
              // Every entry is a line on the same sheet; only the ones you can
              // act on take a hover wash and a chip.
              // A live "now" row gets a marigold wash instead of a border:
              // ink on paper never had 2px rings.
              const shell = `dotted-rule relative bg-bottom bg-repeat-x last:bg-none ${dim} ${
                status === 'now' ? 'bg-wm-marigold/10 rounded-2xl' : ''
              }`;

              if (item.kind === 'breakout' && item.slot) {
                const topic = topicFor(item.slot);
                return (
                  <li key={`${item.day}-${item.sequence}`} className={shell}>
                    {mark}
                    <button
                      type="button"
                      onClick={() => setOpenSlot(item.slot!)}
                      className={`${rowClass} group hover:bg-wm-cream/60 -mx-3 rounded-2xl px-3 transition-colors`}
                    >
                      <Time item={item} />
                      <span className="flex min-w-0 grow items-center gap-3">
                        {/* Her pick wears its speaker's face. With no pick yet,
                            the row wears ALL of them: the breakouts are the
                            reason to come, and a woman deciding whether to sign
                            up was the one person this row told nothing. Not
                            ghosted — she is allowed to read it, and the sheet
                            behind it is open to everyone. Only choosing needs a
                            registration. */}
                        {topic ? (
                          <Avatar
                            name={topic.speaker.name}
                            imageGuid={topic.speaker.imageGuid}
                            imageSrc={topic.speaker.imageSrc}
                            size={34}
                          />
                        ) : (
                          <TopicFaces topics={topics} size={34} />
                        )}
                        <span className="min-w-0">
                          <span className="wm-display block text-2xl">
                            {topic ? topic.shortTitle : isRegistered ? 'Pick a topic' : item.title}
                            <StatusChip status={status} />
                          </span>
                          <span className="text-wm-sage-500 block text-xs">
                            {topic
                              ? `${item.title} · ${topic.speaker.name} · ${topic.room ?? 'Room to be announced'}`
                              : isRegistered
                                ? item.title
                                : topicTease}
                          </span>
                        </span>
                      </span>
                      <Chip>{editable ? 'Change' : guestCanRegister ? 'Choose' : 'Details'}</Chip>
                    </button>
                  </li>
                );
              }

              if (item.kind === 'meal') {
                const inner = (
                  <>
                    <Time item={item} />
                    <span className="flex min-w-0 grow items-center gap-3">
                      {/* Square where the speakers are round: a plate is not a
                          person, and the shape says which is which at 34px. */}
                      {lunchOption ? (
                        <LunchThumb option={lunchOption} size={34} />
                      ) : (
                        <LunchThumbs size={34} />
                      )}
                      <span className="min-w-0">
                        <span className="wm-display block text-2xl">
                          {item.title}
                          <StatusChip status={status} />
                        </span>
                        <span className="text-wm-sage-500 block text-xs">
                          {lunchLabel
                            ? `Your choice: ${lunchLabel}`
                            : `${meals.options.length} to pick from, chosen when you sign up`}
                          {item.room ? ` · ${item.room}` : ''}
                        </span>
                      </span>
                    </span>
                    {editable || guestCanRegister ? (
                      <Chip>{editable ? 'Change' : 'Choose'}</Chip>
                    ) : null}
                  </>
                );
                const interactive = `${rowClass} -mx-3 rounded-2xl px-3 transition-colors hover:bg-wm-cream/60`;
                return (
                  <li key={`${item.day}-${item.sequence}`} className={shell}>
                    {mark}
                    {editable ? (
                      <button
                        type="button"
                        onClick={() => setLunchOpen(true)}
                        className={interactive}
                      >
                        {inner}
                      </button>
                    ) : guestCanRegister ? (
                      <button type="button" onClick={onRegister} className={interactive}>
                        {inner}
                      </button>
                    ) : (
                      <div className={rowClass}>{inner}</div>
                    )}
                  </li>
                );
              }

              /**
               * A GAP IS A LINE ON THE SHEET LIKE ANY OTHER.
               *
               * These used to render a size down in the sans, on the theory
               * that a walk between rooms is not a session and the type should
               * say so. That was wrong twice over.
               *
               * It was wrong about the row: "Break" is on the printed programme
               * in the same column as everything else, and demoting it here
               * made the page disagree with the paper in her hand for no reason
               * she could see.
               *
               * And it was wrong about which rows it would catch. Four of the
               * five blocks titled "Break" are `travel` and one is `break`, so
               * a rule keyed on kind scattered two different type treatments
               * across five identically-titled rows. Whatever the theory, the
               * result on screen was arbitrary.
               *
               * They still read as quieter than a session, and by the things
               * that actually distinguish them: no face, no room, no chip to
               * press, and their own sprig. The weight is not carrying that.
               *
               * Both kinds render identically because they read identically:
               * time she is not expected anywhere. They stay separate kinds
               * because only one means "you have to be somewhere else when this
               * ends" — Friday's twenty minutes are in the room she is already
               * sitting in (0143) — and the agenda editor still needs to tell
               * them apart even when the sheet does not.
               */
              if (item.kind === 'travel' || item.kind === 'break') {
                return (
                  <li key={`${item.day}-${item.sequence}`} className={shell}>
                    {mark}
                    <div className={rowClass}>
                      <Time item={item} />
                      <span className="min-w-0 grow">
                        <span className="wm-display block text-2xl">
                          {item.title}
                          {/* Now earned here too. During the weekend the live
                              row is the answer to "where am I meant to be", and
                              "nowhere, for twenty minutes" is an answer. */}
                          <StatusChip status={status} />
                        </span>
                        {item.description ? (
                          <span className="text-wm-sage-500 block text-xs">{item.description}</span>
                        ) : null}
                      </span>
                    </div>
                  </li>
                );
              }

              const speaker = item.speakerKey ? speakers[item.speakerKey] : undefined;
              return (
                <li key={`${item.day}-${item.sequence}`} className={shell}>
                  {mark}
                  <div className={rowClass}>
                    <Time item={item} />
                    <span className="flex min-w-0 grow items-center gap-3">
                      {speaker ? (
                        <Avatar
                          name={speaker.name}
                          imageGuid={speaker.imageGuid}
                          imageSrc={speaker.imageSrc}
                          size={34}
                        />
                      ) : null}
                      <span className="min-w-0">
                        <span className="wm-display block text-2xl">
                          {item.title}
                          <StatusChip status={status} />
                        </span>
                        {/* Name, then blurb, then room. The portrait alone is a
                            quiz: 686 women are meeting most of these faces for
                            the first time, and the one thing they came for is
                            knowing who is teaching. */}
                        {speaker || item.description || item.room ? (
                          <span className="text-wm-sage-500 block text-xs">
                            {[speaker?.name, item.description, item.room]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        ) : null}
                      </span>
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      ))}

      {/* Slot picker: the six topics, scoped to the slot that was tapped. */}
      <Sheet
        open={openSlot !== null}
        onClose={() => setOpenSlot(null)}
        title={slotGroup?.label ?? 'Breakout'}
        band={
          slotGroup ? <SheetBand kicker={slotGroup.agendaHint} title={slotGroup.label} /> : null
        }
      >
        {openSlot !== null && slotGroup ? (
          <div className="flex flex-col gap-4 p-6 sm:px-7">
            <div>
              <p className="text-wm-sage-700 text-sm">
                You attend one topic in each of Saturday's two sessions. Every topic is taught in
                both, so pick a different one for each.
              </p>
              {problem ? (
                <p className="text-wm-plum mt-2 text-sm font-semibold">{problem}</p>
              ) : null}
            </div>
            <SlotChoices
              topics={topics}
              slot={openSlot}
              pickedSlug={pickFor(openSlot)}
              phase={phase}
              editable={editable}
              guestCanRegister={guestCanRegister}
              onChoose={(topic) => void setBreakout(openSlot, topic)}
              onRegister={onRegister}
            />
          </div>
        ) : null}
      </Sheet>

      {/* Lunch picker, same move. */}
      <Sheet
        open={lunchOpen}
        onClose={() => setLunchOpen(false)}
        title="Saturday lunch"
        band={<SheetBand kicker="Saturday, 12 pm" title="Lunch" />}
      >
        <div className="flex flex-col gap-4 p-6 sm:px-7">
          {problem ? <p className="text-wm-plum text-sm font-semibold">{problem}</p> : null}
          <LunchPicker current={lunchPick} onPick={(value) => void setLunch(value)} />
        </div>
      </Sheet>
    </div>
  );
}

/** Which drawn mark each kind of entry wears in the margin. */
const MARKS: Record<string, 'leaf' | 'bud' | 'bloom' | 'berry' | 'seed'> = {
  doors: 'seed',
  worship: 'bud',
  session: 'leaf',
  breakout: 'bloom',
  meal: 'berry',
  close: 'seed',
  // The quietest mark there is; a walk should not bloom.
  travel: 'seed',
  break: 'seed',
  reception: 'berry',
};

function Time({ item }: { item: AgendaItem }) {
  return (
    <span className="wm-display text-wm-sage-500 w-36 shrink-0 text-base italic tabular-nums">
      {clockLabel(item.start)}
      {item.end ? ` – ${clockLabel(item.end)}` : ''}
    </span>
  );
}

/** The only chrome left on a row: a soft glass pill for the things you can
 *  actually change. */
function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="wm-glass text-wm-sage-700 shrink-0 self-start rounded-full px-3.5 py-1.5 text-xs font-semibold sm:self-center">
      {children}
    </span>
  );
}

function StatusChip({ status }: { status: AgendaStatus | null }) {
  if (status === 'now')
    return <span className="wm-badge wm-badge-almost ml-2 align-middle">Happening now</span>;
  if (status === 'next')
    return <span className="wm-badge wm-badge-open ml-2 align-middle">Up next</span>;
  return null;
}
